import React, { useEffect } from 'react';
import clsx from 'clsx';
import { Product } from '@interfaces/models/product';
import { useBucketTestEvents } from '@hooks/bucket-test/use-bucket-test-events';
import Environment from '@config/index';
import { useContextSelector } from 'use-context-selector';
import { ModalContext } from '@context/modal.context';
import { BNPLTypes } from '@interfaces/models/internal/analytics-event/cart-event';
import Icon from '@components/atoms/icon/icon';
import { useTranslation } from 'next-i18next';
import ModalPaymentInstallment from './modal-payment-installment/modal-payment-installment';
import styles from './product-installment.module.scss';

interface ProductInstallmentProps {
  product: Product;
}

export const acceptedPaymentProviders: BNPLTypes[] = ['facilypay_oney', 'klarna', 'splitit', 'affirm'];

const ProductInstallment: React.FC<ProductInstallmentProps> = ({ product }) => {
  const { t: tA11y } = useTranslation('a11y');
  const paymentProvider = product?.installment?.mnemonic;
  const { sendBNPLClickEvent, sendBNPLShowEvent } = useBucketTestEvents(paymentProvider as BNPLTypes);
  const openModal = useContextSelector(ModalContext, (v) => v.openModal);

  // Check if payment provider is set and included in array
  const isClickable = !!paymentProvider && acceptedPaymentProviders.includes(paymentProvider as BNPLTypes);

  const openPaymentInstallmentModal = () =>
    openModal(
      // TODO:ACCESSIBILITY modalDialogTitleId
      <ModalPaymentInstallment
        paymentName={paymentProvider}
        price={product.price}
      />,
      {
        styles: {
          content: {
            maxWidth: paymentProvider === 'facilypay_oney' ? '100%' : '600px',
            width: '100%',
          },
        },
        onAfterOpen: () => {
          sendBNPLClickEvent('old');
        },
      },
    );

  useEffect(() => {
    if (product.installment) {
      sendBNPLShowEvent('old');
    }
  }, [product]);

  return (
    <>
      {product.installment && (
        <div
          className={clsx(styles.productInstallment, isClickable && styles.productInstallment__link)}
          {...(isClickable && { onClick: openPaymentInstallmentModal })}
          role="presentation"
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={isClickable ? 0 : -1}
        >
          {/* Text */}
          <p className={styles.productInstallment__text}>
            {product.installment.description.replace(product?.installment?.iconPlaceholder, '')}
          </p>
          <p className={styles['productInstallment__text--unbreakable']}>
            <img
              className={clsx(styles.productInstallment__svg, styles[`productInstallment__svg--${paymentProvider}`])}
              src={product.installment?.icon ? `${Environment.imagesBaseUrl}${product.installment.icon}` : ''}
              alt={product.installment?.name}
              width={36}
              height={15}
            />
            {isClickable && paymentProvider === 'affirm' && (
              <Icon
                className={styles.productInstallment__infoIcon}
                name="infobox-default"
                data-cy="affirm_info_icon"
                ariaLabel={tA11y('A11Y.ICON.INFO')}
              />
            )}
          </p>
        </div>
      )}
    </>
  );
};

export default ProductInstallment;
